import type { ApolloClient } from "@apollo/client";
import { CreateOrganizationalRecordDocument, CreateOrganizationalRecordDocumentDocument } from "@/graphql/generated/operations";

/** A file as `ingestFiles` hands it over: base64 content without the `data:` prefix. */
export type OrganizationalRecordFile = { name: string; content: string; mimeType: string };

export type FiledOrganizationalRecordDocument = { id: string; title: string; fileName: string };

// Record contents never enter the shared Apollo cache (organizational-record.graphql is read
// with no-cache throughout), so these writes don't either.

/**
 * The record of a person, created on first use. Genesis answers an existing record instead of
 * failing, so a matched person in an import or a second upload reuses it.
 */
export async function ensureOrganizationalRecord(client: ApolloClient, personId: string): Promise<string> {
  const { data } = await client.mutate({ mutation: CreateOrganizationalRecordDocument, variables: { personId }, fetchPolicy: "no-cache" });
  const record = data?.createOrganizationalRecord;
  if (!record || record.subjectPersonId !== personId) throw new Error("Organizational record was not confirmed");
  return record.id;
}

/** Stores one file in a record, titled by its file name like a manual upload. */
export async function fileOrganizationalRecordDocument(
  client: ApolloClient,
  recordId: string,
  file: OrganizationalRecordFile,
): Promise<FiledOrganizationalRecordDocument> {
  const { data } = await client.mutate({
    mutation: CreateOrganizationalRecordDocumentDocument,
    fetchPolicy: "no-cache",
    variables: { input: { recordId, title: file.name, fileName: file.name, content: { content: file.content, mimeType: file.mimeType } } },
  });
  if (!data?.createOrganizationalRecordDocument) throw new Error("Document upload was not confirmed");
  return { id: data.createOrganizationalRecordDocument.id, title: file.name, fileName: file.name };
}
